import styled from '@emotion/styled'

export const DatePickerContainer = styled.div`
  position: relative;
`

export const DatePickerTrigger = styled.button<{ $invalid?: boolean }>`
  display: flex;
  width: 100%;
  height: 48px;
  padding: 0 14px;
  border: 2px solid ${({ $invalid }) => ($invalid ? '#e53935' : '#596170')};
  border-radius: 6px;
  align-items: center;
  justify-content: space-between;
  outline: none;
  color: #202327;
  background: #fff;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #7d8798;
  }

  &:focus-visible {
    border-color: #ff8e00;
    box-shadow: 0 0 0 3px rgba(255, 142, 0, 0.2);
  }

  &:disabled {
    color: #717782;
    background: #d9dce1;
    cursor: not-allowed;
  }
`

export const CalendarMark = styled.span`
  position: relative;
  width: 17px;
  height: 16px;
  border: 2px solid #353c4b;
  border-radius: 3px;

  &::before {
    position: absolute;
    top: 3px;
    right: 0;
    left: 0;
    border-top: 2px solid #353c4b;
    content: '';
  }
`

export const CalendarPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 100;
  width: min(330px, calc(100vw - 40px));
  padding: 16px;
  border: 1px solid #596170;
  border-radius: 10px;
  color: #fff;
  background: #202327;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.45);
`

export const CalendarHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;

  strong {
    text-align: center;
    text-transform: capitalize;
  }
`

export const CalendarNavigationButton = styled.button`
  min-width: 72px;
  height: 38px;
  padding: 0 10px;
  border: 0;
  border-radius: 7px;
  color: #fff;
  background: #353c4b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover:not(:disabled),
  &:focus-visible {
    outline: none;
    background: #533bff;
  }

  &:disabled {
    color: #717782;
    cursor: not-allowed;
  }
`

export const CalendarWeek = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 5px;
`

export const CalendarWeekday = styled.span`
  padding: 4px 0;
  color: #b7c1d3;
  text-align: center;
  font-size: 11px;
  text-transform: uppercase;
`

export const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
`

export const CalendarDayContainer = styled.div`
  position: relative;
  height: 44px;

  &:hover [role='tooltip'],
  &:focus-within [role='tooltip'] {
    opacity: 1;
    transform: translate(-50%, 0);
    visibility: visible;
  }
`

export const CalendarDay = styled.button<{
  $selected?: boolean
  $today?: boolean
  $kind?: 'holiday' | 'special'
  $weekend?: boolean
}>`
  display: flex;
  height: 44px;
  padding: 4px 2px;
  border: 1px solid ${({ $today }) => ($today ? '#ff8e00' : 'transparent')};
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${({ $selected }) => ($selected ? '#202327' : '#fff')};
  background: ${({ $selected, $kind, $weekend }) => {
    if ($selected) return '#ff8e00'
    if ($kind === 'holiday') return 'rgba(229, 57, 53, 0.22)'
    if ($kind === 'special') return 'rgba(83, 59, 255, 0.25)'
    if ($weekend) return 'rgba(255, 255, 255, 0.07)'
    return 'transparent'
  }};
  box-shadow: ${({ $today }) =>
    $today ? '0 0 0 2px rgba(255, 142, 0, 0.35)' : 'none'};
  cursor: pointer;
  width: 100%;
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};

  &:hover:not(:disabled),
  &:focus-visible {
    outline: none;
    background: ${({ $selected }) => ($selected ? '#ff8e00' : '#533bff')};
  }

  &:disabled {
    color: #596170;
    cursor: not-allowed;
  }
`

export const CalendarBlank = styled.span`
  height: 44px;
`

export const CalendarDayIndicators = styled.span`
  display: flex;
  height: 5px;
  gap: 3px;
  align-items: center;
  margin-top: 2px;
`

export const CalendarDayIndicator = styled.span<{
  $type: 'today' | 'holiday' | 'special'
}>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ $type }) => {
    if ($type === 'today') return '#ff8e00'
    if ($type === 'holiday') return '#ff5c57'
    return '#8d7aff'
  }};
`

export const CalendarDayTooltip = styled.span`
  position: absolute;
  z-index: 10;
  bottom: calc(100% + 8px);
  left: 50%;
  width: max-content;
  max-width: min(220px, calc(100vw - 40px));
  padding: 7px 9px;
  border: 1px solid #4c5669;
  border-radius: 6px;
  color: #fff;
  background: #11151c;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  opacity: 0;
  pointer-events: none;
  text-align: center;
  transform: translate(-50%, 4px);
  transition:
    opacity 120ms ease,
    transform 120ms ease,
    visibility 120ms ease;
  visibility: hidden;
  white-space: normal;

  &::after {
    position: absolute;
    top: 100%;
    left: 50%;
    border: 5px solid transparent;
    border-top-color: #11151c;
    content: '';
    transform: translateX(-50%);
  }
`

export const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #353c4b;
`

export const CalendarLegendItem = styled.span<{
  $type: 'today' | 'holiday' | 'special' | 'weekend'
}>`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #b7c1d3;
  font-size: 10px;

  &::before {
    width: 8px;
    height: 8px;
    border: ${({ $type }) => ($type === 'today' ? '2px solid #ff8e00' : '0')};
    border-radius: 3px;
    background: ${({ $type }) => {
      if ($type === 'holiday') return '#ff5c57'
      if ($type === 'special') return '#8d7aff'
      if ($type === 'weekend') return '#596170'
      return 'transparent'
    }};
    box-sizing: border-box;
    content: '';
  }
`
